import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';

import { useEmitirInformeFinal } from '@/features/informes/hooks/useInformes';
import type { Profesional } from '@/features/profesionales/types';
import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage, isApiError } from '@/lib/apiError';
import { CanalEnvio } from '@/types/enums';

import { normalizePhone } from '@/lib/phone';

import { useEnviarInformeFinal, useEnviarInformeParcial } from '../hooks/useEnvios';
import { useEnviarInformeFinalWhatsApp, useEnviarInformeParcialWhatsApp } from '../hooks/useEnviosWhatsApp';
import { isValidE164, NumerosAdicionalesList } from './NumerosAdicionalesList';

interface EnviarInformeDialogProps {
  open: boolean;
  onClose: () => void;
  uidProtocolo: string;
  finalYaEmitido: boolean;
  numeroProtocolo: number;
  establecimientoNombre: string;
  especie?: string;
  categoria?: string;
  profesional: Profesional;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function parseEmails(raw: string): string[] {
  return raw
    .split(/[\n,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);
}

function getWhatsAppErrorMessage(code: string): string {
  switch (code) {
    case 'whatsapp_envio_fallido':
      return 'No se pudo enviar el mensaje. Reintentá más tarde.';
    case 'whatsapp_template_no_encontrado':
      return 'Error de configuración del laboratorio. Contactá al administrador.';
    case 'whatsapp_numero_invalido':
      return 'Un número no pudo recibir el mensaje. Verificá el formato.';
    default:
      return getErrorMessage(new Error(code));
  }
}

export function EnviarInformeDialog({
  open,
  onClose,
  uidProtocolo,
  finalYaEmitido,
  numeroProtocolo,
  establecimientoNombre,
  especie,
  categoria,
  profesional,
}: EnviarInformeDialogProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const notify = useNotify();

  const [canal, setCanal] = useState<CanalEnvio>(
    profesional.envio_informe ?? CanalEnvio.EMAIL,
  );
  const [profesionalChecked, setProfesionalChecked] = useState(true);
  const [emailsAdicionalesText, setEmailsAdicionalesText] = useState('');
  const [emailFieldError, setEmailFieldError] = useState<string | null>(null);
  const [numerosAdicionales, setNumerosAdicionales] = useState<string[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [marcarComoFinal, setMarcarComoFinal] = useState(false);
  const [sendError, setSendError] = useState<unknown>(null);

  const enviarParcial = useEnviarInformeParcial();
  const enviarFinal = useEnviarInformeFinal();
  const enviarParcialWhatsApp = useEnviarInformeParcialWhatsApp();
  const enviarFinalWhatsApp = useEnviarInformeFinalWhatsApp();
  const emitirFinal = useEmitirInformeFinal();

  useEffect(() => {
    if (!open) {
      setCanal(profesional.envio_informe ?? CanalEnvio.EMAIL);
      setProfesionalChecked(true);
      setEmailsAdicionalesText('');
      setEmailFieldError(null);
      setNumerosAdicionales([]);
      setHasAttemptedSubmit(false);
      setMarcarComoFinal(false);
      setSendError(null);
      enviarParcial.reset();
      enviarFinal.reset();
      enviarParcialWhatsApp.reset();
      enviarFinalWhatsApp.reset();
      emitirFinal.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleCanalChange(newCanal: CanalEnvio): void {
    setCanal(newCanal);
    setProfesionalChecked(true);
    setEmailsAdicionalesText('');
    setEmailFieldError(null);
    setNumerosAdicionales([]);
    setHasAttemptedSubmit(false);
    setSendError(null);
  }

  const isPending =
    enviarParcial.isPending ||
    enviarFinal.isPending ||
    enviarParcialWhatsApp.isPending ||
    enviarFinalWhatsApp.isPending ||
    emitirFinal.isPending;

  // ── Derived state ──────────────────────────────────────────────────────────

  const profesionalTieneEmail = profesional.emails.length > 0;
  const profesionalTieneWhatsApp = !!profesional.telefono_whatsapp;

  const profesionalDisabled =
    canal === CanalEnvio.EMAIL ? !profesionalTieneEmail : !profesionalTieneWhatsApp;

  const profesionalTooltip =
    canal === CanalEnvio.EMAIL
      ? 'El profesional no tiene email cargado.'
      : 'El profesional no tiene WhatsApp cargado. Agregálo en su ficha.';

  // ── Validation ─────────────────────────────────────────────────────────────

  function validateEmail(): boolean {
    const emails = parseEmails(emailsAdicionalesText);
    const invalidos = emails.filter((e) => !isValidEmail(e));
    if (invalidos.length > 0) {
      setEmailFieldError(`Emails inválidos: ${invalidos.join(', ')}`);
      return false;
    }
    const totalDestinatarios =
      (profesionalChecked && profesionalTieneEmail ? 1 : 0) + emails.length;
    if (totalDestinatarios === 0) {
      setEmailFieldError('Agregá al menos un destinatario.');
      return false;
    }
    setEmailFieldError(null);
    return true;
  }

  function validateWhatsApp(): boolean {
    const numeros = numerosAdicionales;
    const invalidos = numeros.filter((n) => !n || !isValidE164(n));
    const normalized = numeros.map((n) => normalizePhone(n));
    const hasDuplicates = normalized.some(
      (n, i) => n && normalized.some((m, j) => j !== i && m === n),
    );
    const totalDestinatarios =
      (profesionalChecked && profesionalTieneWhatsApp ? 1 : 0) + numeros.length;

    if (invalidos.length > 0 || hasDuplicates) return false;
    if (totalDestinatarios === 0) return false;
    return true;
  }

  // ── Send ───────────────────────────────────────────────────────────────────

  async function doEnviarFinal(): Promise<void> {
    if (canal === CanalEnvio.EMAIL) {
      const emailsAdicionales = parseEmails(emailsAdicionalesText);
      await enviarFinal.mutateAsync({
        uidProtocolo,
        body: {
          enviar_al_profesional: profesionalChecked,
          ...(emailsAdicionales.length > 0 && { emails_adicionales: emailsAdicionales }),
        },
      });
    } else {
      await enviarFinalWhatsApp.mutateAsync({
        uidProtocolo,
        body: {
          enviar_al_profesional: profesionalChecked,
          ...(numerosAdicionales.length > 0 && {
            adicionales: numerosAdicionales.map((t) => ({
              telefono: normalizePhone(t) ?? t,
            })),
          }),
        },
      });
    }
  }

  async function doEnviarParcial(): Promise<void> {
    if (canal === CanalEnvio.EMAIL) {
      const emailsAdicionales = parseEmails(emailsAdicionalesText);
      await enviarParcial.mutateAsync({
        uidProtocolo,
        body: {
          enviar_al_profesional: profesionalChecked,
          ...(emailsAdicionales.length > 0 && { emails_adicionales: emailsAdicionales }),
        },
      });
    } else {
      await enviarParcialWhatsApp.mutateAsync({
        uidProtocolo,
        body: {
          enviar_al_profesional: profesionalChecked,
          ...(numerosAdicionales.length > 0 && {
            adicionales: numerosAdicionales.map((t) => ({
              telefono: normalizePhone(t) ?? t,
            })),
          }),
        },
      });
    }
  }

  async function handleConfirm(): Promise<void> {
    setHasAttemptedSubmit(true);
    setSendError(null);

    const isValid =
      canal === CanalEnvio.EMAIL ? validateEmail() : validateWhatsApp();
    if (!isValid) return;

    try {
      if (finalYaEmitido) {
        await doEnviarFinal();
        notify.success('Envío encolado correctamente.');
        onClose();
      } else if (marcarComoFinal) {
        await emitirFinal.mutateAsync(uidProtocolo);
        try {
          await doEnviarFinal();
          notify.success('Envío encolado correctamente.');
          onClose();
        } catch {
          notify.warning(
            'El protocolo quedó cerrado, pero el envío falló. Reintentá desde la sección Envíos.',
          );
          onClose();
        }
      } else {
        await doEnviarParcial();
        notify.success('Envío encolado correctamente.');
        onClose();
      }
    } catch (err) {
      if (isApiError(err) && err.isConflict()) {
        setSendError(new Error('El protocolo está cerrado o el informe final no fue emitido.'));
      } else if (isApiError(err) && err.errorCode) {
        setSendError(new Error(getWhatsAppErrorMessage(err.errorCode)));
      } else {
        setSendError(err);
      }
    }
  }

  // ── Labels / state derivations ─────────────────────────────────────────────

  const isMarcarFinal = !finalYaEmitido && marcarComoFinal;

  const buttonLabel = finalYaEmitido
    ? isPending
      ? 'Enviando...'
      : 'Enviar final'
    : marcarComoFinal
      ? isPending
        ? 'Procesando...'
        : 'Cerrar y enviar final'
      : isPending
        ? 'Enviando...'
        : canal === CanalEnvio.EMAIL
          ? 'Enviar parcial'
          : 'Enviar parcial';

  const buttonIcon = isPending ? (
    <CircularProgress size={16} color="inherit" />
  ) : isMarcarFinal ? (
    <LockIcon />
  ) : canal === CanalEnvio.WHATSAPP ? (
    <WhatsAppIcon />
  ) : (
    <SendIcon />
  );

  const descriptionLine = [
    `Protocolo N° ${numeroProtocolo}`,
    establecimientoNombre,
    especie,
    categoria,
  ]
    .filter(Boolean)
    .join(' — ');

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={isPending ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Enviar informe</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {descriptionLine}
        </Typography>

        {/* Canal selector */}
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend" sx={{ mb: 0.5 }}>
            Canal
          </FormLabel>
          <RadioGroup
            row
            value={canal}
            onChange={(e) => handleCanalChange(e.target.value as CanalEnvio)}
          >
            <FormControlLabel
              value={CanalEnvio.EMAIL}
              control={<Radio disabled={isPending} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 18 }} />
                  <span>Email</span>
                </Box>
              }
            />
            <FormControlLabel
              value={CanalEnvio.WHATSAPP}
              control={<Radio disabled={isPending} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WhatsAppIcon sx={{ fontSize: 18 }} />
                  <span>WhatsApp</span>
                </Box>
              }
            />
          </RadioGroup>
          <Typography variant="caption" color="text.secondary">
            {canal === CanalEnvio.EMAIL
              ? 'El informe se envía como archivo adjunto al email.'
              : 'El informe se envía como documento adjunto en WhatsApp. El destinatario lo recibe directamente en la conversación.'}
          </Typography>
        </FormControl>

        {/* Destinatarios */}
        <Box mb={2}>
          <Typography variant="body2" fontWeight={500} mb={1}>
            Destinatarios
          </Typography>

          {/* Profesional checkbox */}
          <Tooltip
            title={profesionalDisabled ? profesionalTooltip : ''}
            placement="top-start"
            disableHoverListener={!profesionalDisabled}
          >
            <span>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={profesionalChecked}
                    onChange={(e) => setProfesionalChecked(e.target.checked)}
                    disabled={isPending || profesionalDisabled}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {profesional.nombre_apellido}{' '}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {canal === CanalEnvio.EMAIL
                        ? `(${profesional.emails.join(', ') || '(sin email)'})`
                        : `(${profesional.telefono_whatsapp ?? 'sin WhatsApp'})`}
                    </Typography>
                  </Typography>
                }
              />
            </span>
          </Tooltip>

          {/* Additional recipients */}
          {canal === CanalEnvio.EMAIL ? (
            <TextField
              label="Emails adicionales"
              multiline
              minRows={2}
              fullWidth
              placeholder={'uno@mail.com\ndos@mail.com'}
              value={emailsAdicionalesText}
              onChange={(e) => {
                setEmailsAdicionalesText(e.target.value);
                setEmailFieldError(null);
              }}
              disabled={isPending}
              error={!!emailFieldError}
              helperText={emailFieldError ?? 'Uno por línea o separados por coma.'}
              sx={{ mt: 1.5 }}
            />
          ) : (
            <Box mt={1.5}>
              <NumerosAdicionalesList
                numeros={numerosAdicionales}
                onChange={setNumerosAdicionales}
                showErrors={hasAttemptedSubmit}
                disabled={isPending}
              />
              {hasAttemptedSubmit &&
                !profesionalChecked &&
                numerosAdicionales.length === 0 && (
                  <Typography variant="caption" color="error" mt={0.5} display="block">
                    Agregá al menos un destinatario.
                  </Typography>
                )}
            </Box>
          )}
        </Box>

        {/* Marcar como final */}
        {!finalYaEmitido && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: marcarComoFinal
                ? alpha(theme.palette.warning.main, 0.1)
                : 'transparent',
              border: '1px solid',
              borderColor: marcarComoFinal ? 'warning.main' : 'divider',
              transition: 'background-color 0.2s, border-color 0.2s',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={marcarComoFinal}
                  onChange={(e) => setMarcarComoFinal(e.target.checked)}
                  disabled={isPending}
                  color="warning"
                />
              }
              label={
                <Typography variant="body2">
                  Emitir informe final y cerrar el protocolo
                </Typography>
              }
            />
            {marcarComoFinal && (
              <Typography variant="caption" color="text.secondary" display="block" ml={4}>
                Esta acción es irreversible. El protocolo quedará cerrado.
              </Typography>
            )}
          </Box>
        )}

        {!!sendError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {getErrorMessage(sendError)}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancelar
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          color={isMarcarFinal ? 'warning' : 'primary'}
          variant="contained"
          disabled={isPending}
          startIcon={buttonIcon}
        >
          {buttonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
